/**
 * Variant opponents. Seven variants are played by the multi-variant Stockfish, sampled
 * with the same softmax as the standard bot's human levels so mistakes stay plausible.
 * Antichess, which that build does not include, uses a small negamax search over ffish.
 */
import type { Board } from "ffish-es6";
import type { StockfishEngine } from "@/lib/chess/engine";
import { moverCp, pickHumanMove, type Candidate } from "@/lib/chess/bot";
import type { PlayableVariant, VariantLevel } from "./config";
import { enginePosition, legalList, type Ffish } from "./rules";

const WIN = 100_000;

/** Antichess evaluation for the side to move: fewer of your own pieces is better. */
function antichessEval(board: Board): number {
  const placement = board.fen().split(" ")[0];
  let white = 0, black = 0;
  for (const ch of placement) {
    if (/[PNBRQK]/.test(ch)) white++;
    else if (/[pnbrqk]/.test(ch)) black++;
  }
  const mine = board.turn() ? white : black;
  const theirs = board.turn() ? black : white;
  return (theirs - mine) * 100;
}

type SearchState = { nodes: number; budget: number };
class OutOfBudget extends Error {}

function terminalScore(board: Board, ply: number): number | null {
  if (!board.isGameOver(true)) return null;
  const r = board.result(true);
  if (r === "1/2-1/2") return 0;
  const whiteWon = r === "1-0";
  return whiteWon === board.turn() ? WIN - ply : -WIN + ply;
}

function negamax(board: Board, depth: number, alpha: number, beta: number, ply: number, s: SearchState): number {
  if (++s.nodes > s.budget) throw new OutOfBudget();
  const end = terminalScore(board, ply);
  if (end !== null) return end;
  if (depth === 0) return antichessEval(board);
  let best = -Infinity;
  for (const m of legalList(board)) {
    board.push(m);
    const score = -negamax(board, depth - 1, -beta, -alpha, ply + 1, s);
    board.pop();
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  return best;
}

/**
 * Scores every root move by iterative deepening up to `plies`, keeping the deepest
 * iteration that finished within the node budget, then samples like the human levels.
 */
export function searchMove(ff: Ffish, variant: PlayableVariant, fen: string, level: Pick<VariantLevel, "plies" | "temperature" | "slipRate">, rng: () => number = Math.random, budget = 30_000): string {
  const board = new ff.Board(variant.rulesId, fen, !!variant.chess960);
  try {
    const roots = legalList(board);
    if (roots.length === 0) throw new Error("no legal moves");
    if (roots.length === 1) return roots[0];
    let scored: Candidate[] = roots.map((uci) => ({ uci, cp: 0 }));
    const state: SearchState = { nodes: 0, budget };
    for (let depth = 0; depth < level.plies; depth++) {
      try {
        const next: Candidate[] = [];
        for (const { uci } of scored) {
          board.push(uci);
          try { next.push({ uci, cp: -negamax(board, depth, -Infinity, Infinity, 1, state) }); }
          finally { board.pop(); }
        }
        scored = next.sort((a, b) => b.cp - a.cp);
      } catch (err) {
        if (err instanceof OutOfBudget) break;
        throw err;
      }
    }
    // Forced wins and losses must dominate the softmax, as mates do for the engine levels.
    const clamped = scored.map((c) => ({ uci: c.uci, cp: Math.max(-3000, Math.min(3000, c.cp)) }));
    if (level.temperature === undefined) return clamped[0].uci;
    return pickHumanMove(clamped, level.temperature, level.slipRate ?? 0, rng);
  } finally {
    board.delete();
  }
}

export async function chooseVariantMove(
  variant: PlayableVariant,
  game: { startFen: string; moves: readonly string[]; fen: string },
  level: VariantLevel,
  deps: { ff: Ffish; engine: () => StockfishEngine },
  rng: () => number = Math.random,
  signal?: AbortSignal,
): Promise<string> {
  if (variant.engine.kind === "search") return searchMove(deps.ff, variant, game.fen, level, rng);
  const stm = (game.fen.split(" ")[1] ?? "w") as "w" | "b";
  const common = { position: enginePosition(variant, game.startFen, game.moves), variant: variant.engine.uciVariant, chess960: !!variant.chess960, signal };
  if (level.movetime && level.multipv === undefined) {
    const res = await deps.engine().analyse(game.fen, { ...common, movetime: level.movetime });
    if (!res.bestmove) throw new Error("engine returned no move");
    return res.bestmove;
  }
  const res = await deps.engine().analyse(game.fen, { ...common, depth: level.depth, multipv: level.multipv });
  const cands = res.lines.filter((l) => l.pv.length > 0).map((l) => ({ uci: l.pv[0], cp: moverCp(l.score, stm) }));
  if (cands.length === 0) {
    if (res.bestmove) return res.bestmove;
    throw new Error("engine returned no move");
  }
  return pickHumanMove(cands, level.temperature ?? 50, level.slipRate ?? 0, rng);
}
